import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Swaps the "+" chip for the inline add-discount field (and back).
   */
  toggleForm = () => {
    const wrapper = /** @type {HTMLElement | undefined} */ (this.refs.cartDiscountFormWrapper);
    const addButton = /** @type {HTMLElement | undefined} */ (this.refs.cartDiscountAddButton);
    if (!wrapper) return;
    const open = wrapper.classList.toggle('hidden') === false;
    addButton?.classList.toggle('hidden', open);
    if (!open) this.#setViewportZoomLock(false);
    if (open) {
      this.#setViewportZoomLock(true);
      const input = wrapper.querySelector('input');
      input?.focus();
      // After the slot's grow animation (and the mobile keyboard starting to
      // open), bring the field into view: rail to its end horizontally, and
      // vertically within the drawer so the keyboard doesn't cover it
      const rail = wrapper.closest('ul');
      setTimeout(() => {
        rail?.scrollTo({ left: rail.scrollWidth, behavior: 'smooth' });
        input?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 250);
    }
  };

  /**
   * Inside the drawer, a full-section morph would recreate the open <dialog>
   * and break its top-layer state; hydration mode (same as
   * cart-items-component) morphs only the drawer's inner content.
   * @param {string} sectionId
   * @param {string} html
   */
  #morphSectionKeepingDrawerOpen(sectionId, html) {
    // The morph replaces the field with a fresh closed one; release the zoom lock
    this.#setViewportZoomLock(false);
    const inDrawer = !!this.closest('.cart-drawer__dialog');
    morphSection(sectionId, html, { mode: inDrawer ? 'hydration' : 'full' });
  }

  /**
   * iOS auto-zooms inputs with sub-16px text. While the discount field is
   * focused, cap the viewport scale (iOS only — Android keeps pinch-zoom);
   * restore the original viewport when the field closes.
   */
  #setViewportZoomLock(lock) {
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    if (!isIOS) return;
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return;
    if (lock) {
      this.#originalViewport = meta.getAttribute('content') || '';
      if (!this.#originalViewport.includes('maximum-scale')) {
        meta.setAttribute('content', `${this.#originalViewport}, maximum-scale=1`);
      }
    } else if (this.#originalViewport) {
      meta.setAttribute('content', this.#originalViewport);
      this.#originalViewport = '';
    }
  }

  /** @type {string} */
  #originalViewport = '';

  /**
   * Collapses the inline field back to the "+" chip.
   */
  #closeForm() {
    const wrapper = /** @type {HTMLElement | undefined} */ (this.refs.cartDiscountFormWrapper);
    const addButton = /** @type {HTMLElement | undefined} */ (this.refs.cartDiscountAddButton);
    this.#setViewportZoomLock(false);
    if (!wrapper || wrapper.classList.contains('hidden')) return;
    wrapper.classList.add('hidden');
    addButton?.classList.remove('hidden');
  }

  /**
   * Collapses the field back to the "+" chip on Escape, or on blur while empty.
   * @param {KeyboardEvent | FocusEvent} event
   */
  maybeCloseForm = (event) => {
    const input = /** @type {HTMLInputElement | null} */ (event.target instanceof HTMLInputElement ? event.target : null);
    if (event instanceof KeyboardEvent && event.key !== 'Escape') return;
    if (event.type === 'blur' && input?.value.trim()) return;
    this.#closeForm();
  };

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;
    // Empty submit is a no-op (no native `required` bubble)
    if (!discountCodeValue.trim()) return;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#closeForm();
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          discountCode.value = '';
          this.#closeForm();
          this.#handleDiscountError('shipping');
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      this.#morphSectionKeepingDrawerOpen(this.dataset.sectionId, newHtml);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      this.#morphSectionKeepingDrawerOpen(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
